<template>
  <el-config-provider :locale="elLocale">
    <div class="client-main-layout">
      <el-container class="layout-container">
        <!-- 侧边栏 -->
        <el-aside
          :width="sidebarWidth"
          class="sidebar"
          :class="{ 'is-collapse': isCollapse }"
        >
          <div class="logo" @click="toggleCollapse">
            <span v-if="!isCollapse">{{ t("message.system.title") }}</span>            
            <el-icon>
              <Expand v-if="isCollapse" />
              <Fold v-else />
            </el-icon>
          </div>

          <el-menu
            :default-active="activeMenu"
            :default-openeds="defaultOpeneds"
            class="el-menu-vertical"
            :collapse="isCollapse"
            :collapse-transition="false"
            :background-color="menuBgColor"
            :text-color="menuTextColor"
            :active-text-color="menuActiveColor"
            @select="handleMenuSelect"
          >
            <!-- 导航菜单（RTU User 仅显示 RTU 状态入口） -->
            <el-sub-menu v-if="!isRtuUserRole" index="1">
              <template #title>
                <el-icon><Monitor /></el-icon>
                <span>{{ t("message.menu.project") }}</span>
              </template>              
              <!-- 动态渲染菜单项 -->
              <el-menu-item 
                v-for="item in menuItems" 
                :key="item.id"                
                :index="`/clientMain/clientDeviceDeail?businessId=${item.id}&businessName=${item.name}`"
              >
                <span>{{ item.name }}</span>
              </el-menu-item>
            </el-sub-menu>
            <el-menu-item index="/clientMain/rtuDashboard">
              <el-icon><Grid /></el-icon>
              <span>{{ t("message.rtu.fleetTitle") }}</span>
            </el-menu-item>
            <el-sub-menu index="rtu-status">
              <template #title>
                <el-icon><Odometer /></el-icon>
                <span>{{ t("message.rtu.title") }}</span>
              </template>
              <!-- 分组：每组一个可折叠子菜单，成员名后标注偏置 -->
              <el-sub-menu v-for="grp in rtuGroupSections" :key="`rtu-grp-${grp.group}`" :index="`rtu-grp-${grp.group}`">
                <template #title>
                  <el-icon><Share /></el-icon>
                  <span>{{ t("message.rtu.groupLabel", { g: grp.group }) }}</span>
                </template>
                <el-menu-item
                  v-for="d in grp.units"
                  :key="d.unitId"
                  :index="`/clientMain/rtuStatus?unit=${encodeURIComponent(d.unitId)}`"
                >
                  <span>{{ d.name }} ({{ d.offset >= 0 ? '+' : '' }}{{ d.offset }}°)</span>
                </el-menu-item>
              </el-sub-menu>
              <!-- 未分组：直接列出 -->
              <el-menu-item
                v-for="d in rtuUngrouped"
                :key="d.unitId"
                :index="`/clientMain/rtuStatus?unit=${encodeURIComponent(d.unitId)}`"
              >
                <span>{{ d.name }}</span>
              </el-menu-item>
              <el-menu-item v-if="!rtuUnits.length" index="/clientMain/rtuStatus">
                <span>{{ t("message.rtu.title") }}</span>
              </el-menu-item>
            </el-sub-menu>
          </el-menu>
        </el-aside>

        <el-container class="main-container">
          <!-- 顶部导航栏 -->
          <el-header class="header">
            <div class="header-left">
              <el-button
                type="text"
                @click="toggleCollapse"
                class="collapse-btn"
              >
                <el-icon :size="18">
                  <component :is="isCollapse ? 'Expand' : 'Fold'" />
                </el-icon>
              </el-button>

              <el-breadcrumb separator="/" class="breadcrumb">
                <el-breadcrumb-item v-for="item in breadcrumb" :key="item.path">
                  <!-- {{ item.meta?.title || item.name || '' }} -->
                </el-breadcrumb-item>
              </el-breadcrumb>
            </div>

            <div class="header-right">
              <el-button-group class="lang-switch">
                <el-button
                  :type="$i18n.locale === 'zh-CN' ? 'primary' : ''"
                  @click="changeLang('zh-CN')"
                  size="small"
                >
                  中文
                </el-button>
                <el-button
                  :type="$i18n.locale === 'en' ? 'primary' : ''"
                  @click="changeLang('en')"
                  size="small"                                
                >
                  English
                </el-button>
              </el-button-group>

              <el-icon size="20"><UserFilled /></el-icon>
              <el-dropdown @command="handleCommand" trigger="click">
                <span class="user-dropdown">
                  {{ username }}
                  <el-icon><ArrowDown /></el-icon>
                </span>

                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item command="profile">
                      <el-icon><User /></el-icon>
                      {{ t("message.user.profile") }}
                    </el-dropdown-item>
                    <el-dropdown-item command="logout" divided>
                      <el-icon><SwitchButton /></el-icon>
                      {{ t("message.user.logout") }}
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
          </el-header>

          <!-- 主内容区 -->
          <el-main class="main-content">
            <!-- out-in 过渡在生产构建下 leave 阶段偶发卡死（新页面不渲染）——去掉过渡直出 -->
            <router-view v-slot="{ Component }">
              <component :is="Component" />
            </router-view>
          </el-main>
        </el-container>
      </el-container>
    </div>
  </el-config-provider>
</template>

<script setup>
import {
  Fold,
  Expand,
  Monitor,
  Odometer,
  Grid,
  Share,
  User,
  ArrowDown,
  UserFilled,
  SwitchButton,
} from "@element-plus/icons-vue";

import { ref, computed, watch, onMounted,defineExpose } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { ElMessage, ElMessageBox } from "element-plus";
import { useI18n } from "vue-i18n";
import { loginOut,hanldChangeUserlang } from "@/api/UserManager";
import { isRtuUser } from "@/utils/roles";
import { getRtuAdapter } from "@/services/rtuAdapter";
import { getGroupConfig } from "@/services/rtuGroups";
import { protocolbusinessList } from "@/api/ProjectManger";
 

const { t, locale } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

// 动态菜单数据
const menuItems = ref([]);

// 默认展开的菜单项（项目选项）
const defaultOpeneds = ref(['1']);

// 计算属性
const isCollapse = computed(() => store.state.isMenuCollapsed);
const isRtuUserRole = computed(() => isRtuUser());
const rtuUnits = ref([]);
const rtuGroupCfg = ref({});

// RTU 状态子菜单：用户可见的每台 RTU（从机粒度，DTU 透明）一个入口
//（分组的嵌套在组子菜单下）
const loadRtuDevices = async () => {
  try {
    rtuUnits.value = await getRtuAdapter().getUnits();
    rtuGroupCfg.value = await getGroupConfig();
  } catch (e) {
    console.error("加载RTU设备列表失败:", e);
    rtuUnits.value = [];
  }
};

const rtuGroupSections = computed(() => {
  const byGroup = new Map();
  rtuUnits.value.forEach((d) => {
    const cfg = rtuGroupCfg.value[d.unitId];
    if (cfg?.group) {
      if (!byGroup.has(cfg.group)) byGroup.set(cfg.group, []);
      byGroup.get(cfg.group).push({ ...d, offset: cfg.offset });
    }
  });
  return [...byGroup.keys()].sort((a, b) => a - b).map((g) => ({ group: g, units: byGroup.get(g) }));
});

const rtuUngrouped = computed(() =>
  rtuUnits.value.filter((d) => !rtuGroupCfg.value[d.unitId]?.group)
);
const sidebarWidth = computed(() => (isCollapse.value ? "64px" : "200px"));

// 计算当前激活的菜单（包含参数）
// 计算当前激活的菜单（包含参数）
const activeMenu = computed(() => {
  const path = route.path;
  const businessId = route.query.businessId;
  const businessName = route.query.businessName;

  // 如果是设备详情页面且有业务ID参数，返回带参数的完整路径
  if (path === '/main/clientProjectDeail' && businessId) {
    return `${path}?businessId=${businessId}&businessName=${businessName}`;
  }
  return path;
});

const breadcrumb = computed(() => {
  return route.matched
    .filter((item) => item.name)
    .map((item) => ({
      path: item.path,
      meta: item.meta || {},
      name: item.name || "",
    }));
});

const username = computed(() => {
  return store.state.user?.username || t("message.user.guest");
});

const menuBgColor = ref("#304156");
const menuTextColor = ref("#bfcbd9");
const menuActiveColor = ref("#409EFF");
const elLocale = ref(null);

// 加载菜单数据
const loadMenuData = async () => {
  try {
    console.log('开始加载菜单数据...');
    
    const apiData = await fetchMenuDataFromAPI();
    
    // 验证数据有效性
    const validData = Array.isArray(apiData) ? apiData : [];
    
    if (validData.length > 0) {
      menuItems.value = validData;
      console.log('使用API数据,菜单项数量:', menuItems.value.length);
    } else {
      menuItems.value = getDefaultMenuData();
      console.log('使用默认数据，菜单项数量:', menuItems.value.length);
    }
    
    // 设置默认菜单
    setDefaultMenu();
    
  } catch (error) {
    console.error('loadMenuData 错误:', error);
    menuItems.value = getDefaultMenuData();
    setDefaultMenu();
  }
};

const fetchMenuDataFromAPI = async () => {
  try {
    const queryParams = {
      pageNum: 1,
      pageSize: 50,
    }; 
    
    console.log('开始请求菜单数据...');
    const res = await protocolbusinessList(queryParams);
    console.log('API原始响应:', res);
    
    if (res && res.success) {
      // 检查数据格式
      if (res.data && Array.isArray(res.data.list)) {
        const formattedList = res.data.list.map(item => ({
          id: item.id, // 确保有id字段
          name: item.name ||  '未知项目', // 适配不同的字段名
          code: item.code ||  '',
          ...item
        }));        
        console.log('格式化后的菜单数据:', formattedList);
        return formattedList;
      } else {
        console.warn('API返回的数据格式不正确:', res.data);        
        return [];
      }
    } else {
      const errorMsg = res?.msg || '未知错误';
      console.warn('API请求失败:', errorMsg);      
      return [];
    }
  } catch (error) {
    console.error('fetchMenuDataFromAPI 异常:', error);    
    return [];
  }
};
 

// 获取默认菜单数据
const getDefaultMenuData = () => {
  return [
    { id: 1, name: 'Digi-RTU', code: 'DIGI_RTU' },
    { id: 2, name: 'PSP-DXR', code: 'PSP_DXR' },
    { id: 3, name: 'PSP-DXW', code: 'PSP_DXW' },
    { id: 4, name: 'PSP-CIR', code: 'PSP_CIR' }
  ];
};

// 初始化加载语言包
const loadLocale = async (lang) => {
  try {
    const module = await import(
      `element-plus/dist/locale/${lang === "zh-CN" ? "zh-cn" : "en"}.mjs`
    );
    elLocale.value = module.default;
  } catch (error) {
    console.error("Failed to load locale:", error);
    // 回退到英文
    const fallback = await import("element-plus/dist/locale/en.mjs");
    elLocale.value = fallback.default;
  }
};

// 切换语言
const changeLang = async (lang) => {
  try {
    locale.value = lang;
    localStorage.setItem("lang", lang);
    await loadLocale(lang);
    const param = {
    lang: localStorage.getItem('lang') || 'en',
    UserId: 0,
    LangType: locale.value == 'zh-CN'? 1 : 2,
    UserName: ''
    };

    hanldChangeUserlang(param);

    ElMessage.success(t("message.common.langChanged"));
  } catch (error) {
    ElMessage.error(t("message.common.langChangeFailed"));
  }
};

// 初始化加载语言
loadLocale(locale.value);

// 处理菜单选择
const handleMenuSelect = (index) => {
  // 解析路径和参数
  const [path, queryString] = index.split('?');
  const params = new URLSearchParams(queryString);
  const query = {};
  
  for (const [key, value] of params) {
    query[key] = value;
  }
  
  router.push({
    path: path,
    query: query
  });
};

// 默认选中第一个菜单
const setDefaultMenu = () => {
  console.log('当前路由:', route.fullPath);
  console.log('menuItems:', menuItems.value);
  
  // 只有在目标路由且有菜单数据时才处理
  if ((route.path === '/main' || route.path === '/main/clientProjectDeail') && 
      menuItems.value.length > 0) {
    
    const currentBusinessId = route.query.businessId;
    
    // 检查当前businessId是否在menuItems中存在
    const isValidBusinessId = menuItems.value.some(item => item.id == currentBusinessId);
    
    if (!isValidBusinessId) {
      // 使用第一个菜单项
      const firstItem = menuItems.value[0];
      console.log('当前businessId无效,跳转到第一个菜单项:', firstItem.id);
      
      router.push({
        path: '/main/clientProjectDeail',
        query: { businessId: firstItem.id, businessName: firstItem.name}
      });
    } else {
      console.log('当前businessId有效:', currentBusinessId);
    }
  }
};

// const setDefaultMenu = () => {
//   // 如果当前路由是主布局页面且没有业务ID参数，则跳转到第一个菜单项
//   if ((route.path === '/clientMain' || route.path === '/clientMain/clientDeviceDeail') && !route.query.businessId && menuItems.value.length > 0) {
//     const firstItem = menuItems.value[0];
//     router.push({
//       path: '/clientMain/clientDeviceDeail',
//       query: { businessId: firstItem.id }
//     });
//   }
// };

// 监听路由变化，确保菜单状态正确
watch(
  () => route.fullPath,
  () => {
    console.log('路由变化:', route.fullPath, '当前业务ID:', route.query.businessId);
  }
);

// 监听菜单数据变化
watch(menuItems, (newItems) => {
  if (newItems.length > 0) {
    console.log('菜单数据已加载:', newItems);
    // 确保项目选项展开状态
    defaultOpeneds.value = ['1'];
  }
});

// 组件挂载时加载菜单数据
onMounted(() => {
  // RTU User 不显示项目菜单，也无该接口权限（会 403 弹错）——跳过加载
  if (!isRtuUserRole.value) {
    loadMenuData();
  }
  loadRtuDevices();
  // 确保项目选项与 RTU 状态默认展开
  defaultOpeneds.value = ['1', 'rtu-status'];
});

// 方法
const toggleCollapse = () => store.commit("toggleMenu");

const handleCommand = (command) => {
  if (command === "logout") {
    handleWebApiLogout();
  } else if (command === "profile") {
    handleProfile();
  }
};

const handleProfile = () => {
  ElMessage.info(t("message.common.profileDeveloping"));
};

const handleWebApiLogout = async () => {
  try {
    await ElMessageBox.confirm(
      t("message.common.logoutConfirm"),
      t("message.common.tip"),
      {
        confirmButtonText: t("message.common.confirm"),
        cancelButtonText: t("message.common.cancel"),
        type: "warning",
      }
    );

    await handleLogout(false);

  } catch (error) {
    if (error === "cancel") {
      ElMessage.info(t("message.common.logoutCancelled"));
      return;
    }

    ElMessage.error(t("message.common.logoutError"));
    await store.dispatch("logout");
    router.push("/UserLogin");
  }
};

// 注销方法
const handleLogout = async (isPageUnload = false) => {
  try {
    const token = sessionStorage.getItem("authToken");
    if (!token) {
      if (!isPageUnload) {
        router.push("/UserLogin");
      }
      return;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      const response = await loginOut();
      clearTimeout(timeoutId);

      if (!response.success && !isPageUnload) {
        ElMessage.error(t("message.common.logoutFailed"));
      } else if (!isPageUnload) {
        ElMessage.success(t("message.common.logoutSuccess"));
      }
    } catch (error) {
      if (!isPageUnload) {
        ElMessage.error(`${t("message.common.logoutFailed")}: ${error.message}`);
      }
    }

    await store.dispatch("logout");
    if (!isPageUnload) {
      router.push("/UserLogin");
    }
  } catch (error) {
    if (!isPageUnload && error !== "cancel") {
      ElMessage.error(t("message.common.logoutError"));
      await store.dispatch("logout");
      router.push("/UserLogin");
    }
  }
};

// 提供刷新菜单的方法，可以在其他地方调用
const refreshMenu = () => {
  loadMenuData();
};

// 暴露方法，以便在其他地方调用
defineExpose({
  refreshMenu
});
</script>

<style lang="scss" scoped>
.client-main-layout {
  height: 100%;
  overflow: hidden;
  display: flex;

  .layout-container {
    display: flex;
    flex: 1;
    height: 100vh;
    width: 100vw;
    overflow: hidden; // 禁止容器滚动

    .main-container {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0; // 关键修复：允许内容压缩
    }
  }

  .sidebar {
    height: 100vh;
    background-color: v-bind(menuBgColor);
    transition: width 0.3s;
    overflow: hidden;

    &.is-collapse {
      :deep(.el-submenu__title span),
      :deep(.el-menu-item span) {
        height: 0;
        width: 0;
        overflow: hidden;
        visibility: hidden;
        display: inline-block;
      }
    }

    .logo {
      height: 60px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-size: 18px;
      font-weight: bold;
      background-color: #2b2f3a;
      cursor: pointer;

      span {
        margin-left: 10px;
      }

      .el-icon {
        font-size: 24px;
      }
    }

    .el-menu-vertical {
      border-right: none;
      background-color: v-bind(menuBgColor) !important;
      height: calc(100vh - 60px); // 侧边栏高度减去顶部导航栏
      overflow-y: auto; //侧边栏单独滚动

      // 确保项目选项默认展开
      :deep(.el-sub-menu.is-opened) {
        .el-sub-menu__title {
          background-color: rgba(0, 0, 0, 0.1) !important;
        }
      }

      :deep(.el-submenu__title),
      :deep(.el-menu-item) {
        // 允许文本换行
        white-space: normal;
        height: auto !important;
        line-height: 1.5 !important;
        padding-top: 10px !important;
        padding-bottom: 10px !important;

        // 图标和文本对齐
        display: flex;
        align-items: center;

        // 文本容器样式
        span {
          display: inline-block;
          word-break: break-word; // 允许单词内断行
          text-align: left;
        }

        // 图标样式
        .el-icon {
          flex-shrink: 0; // 防止图标被压缩
          margin-right: 8px;
        }
      }
      
      // 折叠状态下保持单行显示
      &.is-collapse {
        :deep(.el-menu-item) {
          white-space: nowrap;
          span {
            display: none;
          }
        }
      }
      
      // 精确控制选中状态
      :deep(.el-menu-item) {
        &.is-active {
          color: v-bind(menuActiveColor) !important;
          background-color: rgba(0, 0, 0, 0.2) !important;
        }
        
        // 非激活状态
        &:not(.is-active) {
          color: v-bind(menuTextColor) !important;
          background-color: transparent !important;
        }
      }
    }
  }

  .main-container {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 40px;
    background-color: #fff;
    box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
    padding: 0 1px;
    z-index: 10; //确保顶部导航栏在内容上方

    .header-left {
      display: flex;
      align-items: center;

      .collapse-btn {
        margin-right: 20px;
      }

      .breadcrumb {
        font-size: 14px;
      }
    }

    .header-right {
      display: flex;
      align-items: center;
      gap: 15px;

      .user-dropdown {
        display: flex;
        align-items: center;
        cursor: pointer;
        font-size: 14px;

        .el-icon {
          margin-left: 5px;
          font-size: 12px;
        }
      }

      .lang-switch {
        margin-left: 15px;

        .el-button {
          padding: 5px 10px;
          min-height: auto;
        }
      }
    }
  }

  .main-content {
    background-color: #f0f2f5;
    padding: 1px;
    flex: 1; // 主内容区自动填充剩余空间
    overflow-y: auto; // 内容区单独滚动
    height: 0; // 配合flex:1实现正确计算

    // 隐藏默认滚动条（仅在不滚动时）
    scrollbar-width: none; /* Firefox */
    &::-webkit-scrollbar {
      display: none; /* Chrome/Safari */
    }

    // 当内容溢出时显示滚动条
    &:hover {
      scrollbar-width: thin; /* Firefox */
      &::-webkit-scrollbar {
        display: block; /* Chrome/Safari */
        width: 6px;
      }
      &::-webkit-scrollbar-thumb {
        background-color: #c1c1c1;
        border-radius: 3px;
      }
    }
  }
}

// 路由过渡动画保持不变
.fade-transform-leave-active,
.fade-transform-enter-active {
  transition: all 0.3s;
}

.fade-transform-enter-from {
  opacity: 0;
  transform: translateX(-30px);
}

.fade-transform-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
</style>